import React, { useEffect, useState } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { RichText } from 'prismic-reactjs';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import Text from '@components/shared/Text';
import { BREAKPOINT } from '@constants';
import { PageSlice, SliceIconTextGridDetails, SliceIconTextGridItem } from '@contracts';
import LinkImage from '@images/proviotics/link_img.svg';
import { detectIncognito } from '@utils/detectIncognito';
import { replaceText } from '@utils/handleTextReplacement';
import useNavigate from '@utils/useNavigate';
import Button from './Button';
import Carousel from './Carousel';
import LinkTranslated from './LinkTranslated';
import PrismicLinkSerializer from './PrismicLinkSerializer';
import useDesktop from './useDesktop';

const carouselOptions = {
  autoPlay: false,
  groupCells: false,
  pauseAutoPlayOnHover: false,
  cellAlign: 'center',
  draggable: true,
  pageDots: true
};

interface Props {
  slice: PageSlice;
  country?: CountryCode;
}

const IconTextGrid: React.FC<Props> = ({ slice, country = CountryCode.GB }) => {
  const primary = slice.primary as SliceIconTextGridDetails;
  const items = slice.items as SliceIconTextGridItem[];

  const { navigateWrapper } = useNavigate();
  const [letterSpacing, setLetterSpacing] = useState('');
  const isDesktop = useDesktop(BREAKPOINT.tablet);
  const location = useLocation();
  const isExpandable = primary?.expandable;
  const [showAll, setShowAll] = useState(!isExpandable);

  const { alignment_desktop, alignment_mobile } = primary;
  const alignment: string = isDesktop ? alignment_desktop : alignment_mobile;
  const defaultPadding = alignment.includes('small') ? '30px 0' : '60px 0';
  const textAlign = alignment === ('image-text-vertical' || null) || alignment === 'carousel' ? 'center' : 'left';
  const mobileCarousel = alignment === 'carousel';

  const gap = (isDesktop ? primary?.gap?.text : primary?.gap_mobile?.text || primary?.gap?.text) || '0';
  const iconGap = (isDesktop ? primary?.icon_gap?.text : primary?.icon_gap_mobile?.text || primary?.icon_gap?.text) || gap;
  const columns = primary?.columns?.text;
  const columnsMobile = primary?.columns_mobile?.text;
  const imageWidth = primary?.image_width?.text ?? '90px';
  const imageHeight = primary?.image_height?.text ?? '90px';
  const backgroundColor = primary?.background_color;
  const backgroundGradient = primary?.background_gradient?.text;
  const textColor = primary?.text_color ?? Colors.forestGreen;
  const gridTitle = primary.grid_title?.text;
  const gridTitleRich = primary.grid_title_rich?.richText;
  const padding = primary?.padding?.text !== '' ? primary?.padding?.text : defaultPadding;
  const mobilePadding = primary?.mobile_padding?.text !== '' ? primary?.mobile_padding?.text : padding;
  const columnMaxWidth = primary?.column_max_width?.text ?? '100%';
  const expandableText = primary?.expandable_text?.text;
  const maxVisibleItems = showAll ? items?.length : Number(primary?.max_visible_items?.text);
  const buttonTitle = primary?.button_title?.text;
  const buttonLink = primary?.button_link?.text;
  const innerMaxWidth =
    (isDesktop ? primary?.inner_max_width?.text : primary?.inner_max_width_mobile?.text || primary?.inner_max_width?.text) || '100%';
  const scrollOnMobile = primary?.scroll_on_mobile ?? false;

  useEffect(() => {
    detectIncognito().then((result) => {
      if (result.browserName === 'Safari') {
        setLetterSpacing('-0.9px');
      }
    });
  }, []);

  const renderTitle = () => {
    if (gridTitleRich?.length > 0 && gridTitleRich[0]?.text !== '') {
      return (
        <TitleWrap $isSmallTitle={primary?.is_small_title}>
          <Text richText={gridTitleRich} textAlign="center" textColor={textColor} />
        </TitleWrap>
      );
    }
    if (gridTitle && gridTitle !== '') {
      return (
        <TitleWrap $isSmallTitle={primary?.is_small_title} $textColor={textColor}>
          <p>{gridTitle}</p>
        </TitleWrap>
      );
    }
    return null;
  };

  const renderIconBox = (item: SliceIconTextGridItem, index: number) => {
    const image = getImage(item.image);
    const imageAlt = replaceText(item.image.alt, country, location) ?? '';
    const itemTitleRichText = item.item_title?.richText;
    const link = item.link?.text;
    const link_text = item.link_text?.text;
    const itemTextHTML = replaceText(item.text.html, country, location);
    const { type_of_link } = item;
    const newTab = item.new_tab;
    const showItem = maxVisibleItems > index;

    return (
      <IconBoxWrap
        className={`align-${alignment}`}
        key={index}
        $columns={Number(columns)}
        $columnsMobile={Number(columnsMobile)}
        $link={!!link}
        $maxWidth={columnMaxWidth}
        $textColor={textColor}
        $textAlign={textAlign}
        $display={showItem ? 'flex' : 'none'}
      >
        <IconBox className={`icon-box align-${alignment}`} $gap={iconGap} $letterSpacing={letterSpacing}>
          <ImgWrap className={`icon-box-img-wrap align-${alignment}`} style={{ width: imageWidth, height: imageHeight, flexShrink: 0 }}>
            {image && <GatsbyImage image={image} alt={imageAlt} objectFit="contain" />}
          </ImgWrap>
          <ContentWrap $isSmallTitle={primary?.is_small_item_title}>
            {itemTitleRichText && (
              <ItemTitle $isSmallTitle={primary?.is_small_item_title} style={{ textAlign }}>
                <RichText render={itemTitleRichText} serializeHyperlink={PrismicLinkSerializer}></RichText>
              </ItemTitle>
            )}
            <>
              {!type_of_link && !newTab && link ? (
                <LinkTranslated to={link} targetBlank={newTab}>
                  <Text text={itemTextHTML} textAlign={textAlign} textColor={textColor} padding="0" />
                </LinkTranslated>
              ) : (
                <Text text={itemTextHTML} textAlign={textAlign} textColor={textColor} padding="0" />
              )}
              {type_of_link && type_of_link === 'internal' ? (
                <InternalLink to={link} $textColor={textColor}>
                  {link_text}
                </InternalLink>
              ) : (
                <>
                  {link_text && (
                    <ExternalLinkWrap>
                      <ExternalLink to={link} targetBlank>
                        {link_text}
                      </ExternalLink>
                      {link_text && <LinkImageStyled />}
                    </ExternalLinkWrap>
                  )}
                </>
              )}
            </>
          </ContentWrap>
        </IconBox>
      </IconBoxWrap>
    );
  };

  return (
    <Container
      className={`align-${alignment}`}
      $backgroundColor={backgroundColor}
      $backgroundGradient={backgroundGradient || ''}
      $padding={padding || ''}
      $mobilePadding={mobilePadding || ''}
    >
      <InnerWrapper
        $width={primary?.inner_width?.text || ''}
        $maxWidth={innerMaxWidth}
        $background={primary?.inner_background?.text || ''}
        $padding={primary?.inner_padding?.text || ''}
        $mobilePadding={primary?.inner_padding_mobile?.text || ''}
        $isBordered={primary?.is_inner_bordered}
        $isTopBorderRounded={primary?.is_inner_top_border_rounded}
        $isBottomBorderRounded={primary?.is_inner_bottom_border_rounded}
        $columnsMobile={Number(columnsMobile)}
        $scrollOnMobile={scrollOnMobile}
        className="icon-text-grid-inner"
      >
        {renderTitle()}
        {!isDesktop && mobileCarousel ? (
          <CarouselWrap>
            <Carousel options={carouselOptions}>{items && items.map((item, index) => renderIconBox(item, index))}</Carousel>
          </CarouselWrap>
        ) : (
          <ItemsWrap className={`align-${alignment}`} $gap={gap} $scrollOnMobile={scrollOnMobile}>
            {items && items.map((item, index) => renderIconBox(item, index))}
          </ItemsWrap>
        )}
        {buttonLink && <StyledButton onClick={() => navigateWrapper(buttonLink)}>{buttonTitle}</StyledButton>}
        {isExpandable && (
          <LinkText onClick={() => setShowAll(!showAll)}>
            {showAll ? <div>{`Show less ${expandableText}`}</div> : <div>{`Show all ${expandableText}`}</div>}
          </LinkText>
        )}
      </InnerWrapper>
    </Container>
  );
};

export default IconTextGrid;

interface ContainerProps {
  $backgroundColor?: string;
  $backgroundGradient?: string;
  $padding?: string;
  $mobilePadding?: string;
  $scrollOnMobile?: boolean;
  $gap?: string;
}

const Container = styled.div<ContainerProps>`
  background-color: ${(props) => (props.$backgroundColor ? props.$backgroundColor : '')};
  background-image: ${(props) => (props.$backgroundGradient ? props.$backgroundGradient : 'none')};
  width: 100%;
  display: flex;
  padding: ${(props) => props.$padding};

  @media (max-width: ${BREAKPOINT.laptop}px) {
    padding: ${(props) => props.$mobilePadding};

    &.align-carousel {
      padding: 0 0 60px 0;
    }
  }
`;

const InnerWrapper = styled.div<{
  $background: string;
  $width: string;
  $maxWidth: string;
  $padding: string;
  $mobilePadding: string;
  $isBordered: boolean;
  $isTopBorderRounded: boolean;
  $isBottomBorderRounded: boolean;
  $columnsMobile?: number;
  $scrollOnMobile?: boolean;
}>`
  display: flex;
  max-width: ${(props) => props.$maxWidth || '1200px'};
  margin: 0 auto;
  width: ${(props) => props.$width || '100%'};
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  padding: ${(props) => props.$padding || '24px 0'};

  @media (max-width: ${BREAKPOINT.laptop}px) {
    ${(props) =>
      props.$scrollOnMobile
        ? `
      overflow-x: scroll;
      display: block;
      padding-bottom: 20px;
      `
        : `
    flex-direction: column;
    padding: ${props.$mobilePadding || '0 20px'};
    display: ${props.$columnsMobile ? 'inline' : 'flex'};
    column-count: ${props.$columnsMobile || 'auto'};
    `};
  }

  ${(props) =>
    props.$isBordered
      ? `
    background-color: ${props.$background || 'none'};

    ${
      props.$isTopBorderRounded
        ? `
      border-top-left-radius: 20px;
      border-top-right-radius: 20px;
    `
        : ''
    }

    ${
      props.$isBottomBorderRounded
        ? `
      border-bottom-left-radius: 20px;
      border-bottom-right-radius: 20px;
    `
        : ''
    }

    @media (max-width: ${BREAKPOINT.laptop}px) {
      ${
        props.$isTopBorderRounded
          ? `
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
      `
          : ''
      }
  
      ${
        props.$isBottomBorderRounded
          ? `
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
      `
          : ''
      }
    }
  `
      : ''}
`;

const LinkText = styled.button`
  all: unset;
  cursor: pointer;
  padding-top: 60px;

  font-family: BrownStd;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;

  border-bottom: 2px solid ${Colors.forestGreen};
`;

const ItemsWrap = styled.div<ContainerProps>`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;
  max-width: 1200px;
  row-gap: 60px;
  gap: ${(props) => props.$gap};

  @media (max-width: ${BREAKPOINT.laptop}px) {
    align-items: center;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    ${(props) =>
      props.$scrollOnMobile
        ? `
    flex-wrap: nowrap;
      flex-direction: row;
      align-items: flex-start;
      max-width: none;
      justify-content: flex-start;
      padding: 0 30px;

      & > div {
        flex: 0 0 auto;
        max-width: 85%;
      }
      `
        : `
      flex-flow: nowrap;
      align-items: center;
      flex-direction: column;

      &.align-image-text-wrap {
        flex-flow: wrap;
      }

    `};
  }
`;

interface IIconBoxWrap {
  $display?: string;
  $columns?: number;
  $columnsMobile?: number;
  $link?: boolean;
  $maxWidth?: string;
  $textColor?: string;
  $textAlign?: string;
  $gap?: string;
  $letterSpacing?: string;
}

const IconBoxWrap = styled.div<IIconBoxWrap>`
  display: ${(props) => props.$display || 'flex'};
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
  flex: ${(props) => (props.$columns && `1 0 ${100 / props.$columns}%`) || '1 0 30%'};
  max-width: ${(props) => props.$maxWidth || '100%'};
  color: ${(props) => (props.$textColor ? props.$textColor : '')};

  p {
    color: ${(props) => (props.$textColor ? props.$textColor : '')};
  }

  ${(props) =>
    props.$link &&
    `
  a > .text-section > *:last-child:after {
    margin-left: 4px;
    display: inline-block;
    content: ' ';
    background-size: 14px 14px;
    height: 14px;
    width: 14px;
    background-image: url('https://ecommerce-website.cdn.prismic.io/ecommerce-website/ece5a313-5677-4c7c-9e9e-b04b0239749b_link_img.svg');
  }
`};

  @media (max-width: ${BREAKPOINT.laptop}px) {
    max-width: 100%;
    padding: 20px 0;
    flex-direction: column;
    align-items: center;
    flex-wrap: nowrap;
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    width: 100%;
    flex: auto;
    padding: 10px 0;
    margin-bottom: ${(props) => (props.$columnsMobile ? '20px' : 'auto')};
  }

  &.align-image-text-wrap {
    width: ${(props) => props.$maxWidth || '100%'};
  }
`;

const IconBox = styled.div<IIconBoxWrap>`
  display: flex;
  flex: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex-grow: 0;
  column-gap: 30px;
  row-gap: 30px;
  p {
    strong,
    b {
      letter-spacing: ${(props) => props.$letterSpacing || '0'};
    }
  }

  @media (max-width: ${BREAKPOINT.laptop}px) {
    flex: auto;
    width: 100%;
    padding: 0;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  p {
    margin-top: 5px;
  }

  .text-section {
    h5 {
      letter-spacing: 1.5px;
    }
  }

  &.small {
    row-gap: 10px;
    column-gap: 10px;

    :first-child {
      padding: 0;
    }

    .text-section {
      padding: 0;
    }

    p {
      max-width: 80%;
      margin-top: 0;
      font-family: BrownStd;
      font-size: 12px;
      font-weight: 700;
      line-height: 16px;
      letter-spacing: 0.75px;
      text-align: left;

      @media (max-width: ${BREAKPOINT.laptop}px) {
        max-width: 100%;
        text-align: center;
        margin: auto;
      }
    }
  }

  &.bordered-small {
    p {
      font-family: BrownStd;
      font-size: 13px;
      font-weight: 400;
      line-height: 150%;
    }
  }

  &.align-image-text-vertical {
    justify-content: center;

    p {
      text-align: ${(props) => props.$textAlign};
    }

    @media (max-width: ${BREAKPOINT.laptop}px) {
      row-gap: 20px;
    }
  }

  &.align-image-text {
    column-gap: ${(props) => props.$gap || '40px'};
    flex-direction: row;
    text-align: left;

    &:not(.bordered-small) {
      @media (max-width: ${BREAKPOINT.tablet}px) {
        gap: ${(props) => props.$gap || '30px'};
        padding: 0;
        justify-content: flex-start;
        align-items: flex-start;
      }
    }
  }

  &.align-text-image {
    flex-direction: row-reverse;
    text-align: left;
    justify-content: space-between;
    margin: 0 3% 60px;

    @media (max-width: ${BREAKPOINT.laptop}px) {
      margin: 0 0 60px;
    }

    @media (max-width: ${BREAKPOINT.tablet}px) {
      margin: 0 0 30px;
      align-items: flex-start;
    }
  }

  &.align-carousel {
    flex-direction: column;
    text-align: center;
    justify-content: space-between;
    margin: 10px 3% 10px;
    row-gap: 0;

    flex: auto;
    width: 100%;
    padding: 30px;
    align-items: center;
    text-align: center;
  }
`;

const ImgWrap = styled.div<{ $width?: string; $height?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => `${props.$width} !important` || '100%'};
  height: ${(props) => `${props.$height}` || 'auto'};
  flex-shrink: 0;

  @media (max-width: ${BREAKPOINT.tablet}px) {
    width: 80%;
    padding: 10px;
    &.bordered-small {
      padding: 5px;
    }
  }

  & > svg {
    width: auto;
    height: auto;
  }

  & > div {
    max-height: 100%;
  }
`;

const ContentWrap = styled.div<{ $isSmallTitle?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  justify-content: inherit;
  gap: 10px;
  p {
    margin-top: 0;
  }

  ${(props) =>
    props.$isSmallTitle
      ? `
      gap: 5px;
  `
      : ''}
`;

const ItemTitle = styled.div<{ $isSmallTitle?: boolean }>`
  color: inherit;
  width: 100%;

  & h6 {
    font-family: 'BrownStd';
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 24px */
    margin-bottom: -5px;
  }

  ${(props) =>
    props.$isSmallTitle
      ? `
    width: 100%;

    p {
      font-family: BrownStd;
      font-weight: 400 !important;
      font-size: 13px;
      line-height: 18px;
      letter-spacing: 1.5px;

      @media (max-width: ${BREAKPOINT.tablet}px) {
        font-size: 11px !important;
        line-height: 10px !important;
      }
    }
  `
      : ''}
`;

interface TitleWrapProps {
  $isSmallTitle?: boolean;
  $textColor?: string;
}

const TitleWrap = styled.div<TitleWrapProps>`
  padding-bottom: 60px;
  color: ${(props) => props.$textColor};
  text-align: center;

  p {
    font-family: Domaine;
    font-size: 50px;
    font-weight: 500;
    line-height: 55px;
    letter-spacing: -0.01em;
    color: inherit;

    @media (max-width: ${BREAKPOINT.laptop}px) {
      font-size: 34px;
      line-height: 37px;
      letter-spacing: -0.02em;
    }
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    padding-bottom: 30px;
  }

  ${(props) =>
    props.$isSmallTitle
      ? `
    padding-bottom: 40px;

    p {
      font-style: normal;
      font-weight: 600;
      font-size: 24px;
      line-height: 140%;
      letter-spacing: -0.02em;

      @media (max-width: ${BREAKPOINT.laptop}px) {
        font-style: normal;
        font-weight: 600;
        font-size: 24px;
        line-height: 140%;
      }
    }
  `
      : ''}
`;

const InternalLink = styled(LinkTranslated)<TitleWrapProps>`
  margin-top: 10px;
  font-family: BrownStd;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  color: ${(props) => props.$textColor};

  border-bottom: 2px solid ${(props) => props.$textColor};

  &:hover {
    text-decoration: none;
    color: ${Colors.pink};
    border-bottom-color: ${Colors.pink};
  }
`;

const ExternalLinkWrap = styled.div`
  margin-top: 10px;
  width: 100%;
`;

const ExternalLink = styled(LinkTranslated)<TitleWrapProps>`
  font-family: BrownStd;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: ${Colors.lilac};

  &:hover {
    text-decoration: underline;
  }
`;

const LinkImageStyled = styled(LinkImage)`
  align-self: center;
  margin-left: 5px;
  vertical-align: sub;
`;

const StyledButton = styled(Button)`
  margin-top: 90px;
`;

const CarouselWrap = styled.div`
  width: 100%;
`;
